import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/site";

const LOGO = "/rangerbase-logo.png";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-warmwhite/85 backdrop-blur-xl border-b border-charcoal/10 py-3" : "bg-warmwhite/40 backdrop-blur-md border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[88rem] px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={LOGO} alt="Ranger Base" className="h-11 w-11 object-contain" />
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold tracking-tight text-charcoal text-lg">RANGER BASE</span>
            <span className="text-[10px] tracking-overline uppercase text-earth mt-1">Land Management Services</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  isActive ? "text-eucalyptus" : "text-charcoal/70 hover:text-eucalyptus"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-eucalyptus text-warmwhite px-5 py-2.5 text-sm font-semibold hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            Work With Us <ArrowUpRight size={15} strokeWidth={2} />
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-charcoal/15 text-charcoal"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-warmwhite border-t border-charcoal/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[{ label: "Home", path: "/" }, ...NAV_LINKS, { label: "Contact", path: "/contact" }].map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 text-xl font-heading font-semibold tracking-tight border-b border-charcoal/5 ${
                      isActive ? "text-eucalyptus" : "text-charcoal"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
